import {
  CompanySchema,
  CompanyCreateSchema,
  CompanyUpdateSchema,
  GetCompaniesParams,
  GetCompanyRequest,
  ContactSchema,
  ContactCreateSchema,
  ContactUpdateSchema,
  CompanyStudySchema,
  PaginatedCompaniesResponse,
} from '@/types/companies';
import { api } from './index';

class CompaniesService {
  // --------------------------------------- V3 - VERSION 3 ------------------------------------------

  /**
   * Get all companies with filtering (V3)
   * GET /v3/companies/
   * NEW IN V3 ✅
   */
  async getCompanies(params?: GetCompaniesParams): Promise<PaginatedCompaniesResponse> {
    try {
      const response = await api.get<PaginatedCompaniesResponse>('/companies/', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching companies (V3):', error);
      throw error;
    }
  }

  /**
   * Get specific company by ID (V3)
   * GET /v3/companies/{company_id}
   * NEW IN V3 ✅
   */
  async getCompany(request: GetCompanyRequest): Promise<CompanySchema> {
    try {
      const response = await api.get<CompanySchema>(`/companies/${request.companyId}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching company ${request.companyId} (V3):`, error);
      throw error;
    }
  }

  /**
   * Create new company (V3)
   * POST /v3/companies/
   * NEW IN V3 ✅
   */
  async createCompany(companyData: CompanyCreateSchema): Promise<CompanySchema> {
    try {
      const response = await api.post<CompanySchema>('/companies/', companyData);
      return response.data;
    } catch (error) {
      console.error('Error creating company (V3):', error);
      throw error;
    }
  }

  /**
   * Update company (V3)
   * PATCH /v3/companies/{company_id}
   * NEW IN V3 ✅
   */
  async updateCompany(companyId: string, companyData: CompanyUpdateSchema): Promise<CompanySchema> {
    try {
      const response = await api.patch<CompanySchema>(`/companies/${companyId}`, companyData);
      return response.data;
    } catch (error) {
      console.error(`Error updating company ${companyId} (V3):`, error);
      throw error;
    }
  }

  /**
   * Delete company (V3)
   * DELETE /v3/companies/{company_id}
   * NEW IN V3 ✅
   */
  async deleteCompany(companyId: string): Promise<void> {
    try {
      await api.delete(`/companies/${companyId}`);
    } catch (error) {
      console.error(`Error deleting company ${companyId} (V3):`, error);
      throw error;
    }
  }

  // ---------------------------------- COMPANY CONTACTS MANAGEMENT ----------------------------------

  /**
   * Get company contacts (V3)
   * GET /v3/companies/{company_id}/contacts
   * NEW IN V3 ✅
   */
  async getCompanyContacts(companyId: string): Promise<ContactSchema[]> {
    try {
      const response = await api.get<ContactSchema[]>(`/companies/${companyId}/contacts`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching contacts for company ${companyId} (V3):`, error);
      throw error;
    }
  }

  /**
   * Create company contact (V3)
   * POST /v3/companies/{company_id}/contacts
   * NEW IN V3 ✅
   */
  async createCompanyContact(
    companyId: string,
    contactData: ContactCreateSchema
  ): Promise<ContactSchema> {
    try {
      const response = await api.post<ContactSchema>(
        `/companies/${companyId}/contacts`,
        contactData
      );
      return response.data;
    } catch (error) {
      console.error(`Error creating contact for company ${companyId} (V3):`, error);
      throw error;
    }
  }

  /**
   * Update company contact (V3)
   * PATCH /v3/companies/{company_id}/contacts/{contact_id}
   * NEW IN V3 ✅
   */
  async updateCompanyContact(
    companyId: string,
    contactId: string,
    contactData: ContactUpdateSchema
  ): Promise<ContactSchema> {
    try {
      const response = await api.patch<ContactSchema>(
        `/companies/${companyId}/contacts/${contactId}`,
        contactData
      );
      return response.data;
    } catch (error) {
      console.error(`Error updating contact ${contactId} for company ${companyId} (V3):`, error);
      throw error;
    }
  }

  /**
   * Delete company contact (V3)
   * DELETE /v3/companies/{company_id}/contacts/{contact_id}
   * NEW IN V3 ✅
   */
  async deleteCompanyContact(companyId: string, contactId: string): Promise<void> {
    try {
      await api.delete(`/companies/${companyId}/contacts/${contactId}`);
    } catch (error) {
      console.error(`Error deleting contact ${contactId} for company ${companyId} (V3):`, error);
      throw error;
    }
  }

  async getFinancials(companyId: string): Promise<any> {
    try {
      const response = await api.get(`/companies/${companyId}/financials`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching financials for company ${companyId} (V3):`, error);
      throw error;
    }
  }

  async getCompanyStudies(companyId: string): Promise<CompanyStudySchema[]> {
    try {
      const response = await api.get(`/companies/${companyId}/studies`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching studies for company ${companyId}:`, error);
      throw error;
    }
  }
}

export const companiesService = new CompaniesService();
